import "pixi";
import "phaser";

import AudioEngine from "./audio_engine";
import * as cursor from "./cursor";
import * as collision from "./collision";
import * as i18n from "./i18n";
import * as logging from "./logging";
import * as platform from "./platform";
import { CUT_WIDTH, FLOAT_EPSILON, MIN_VISIBLE } from "./constants";

const CUT_LINE_COLOR_VALID = 0xFFFFFF;
const CUT_LINE_COLOR_INVALID = 0xFF0000;
const SNAP_ANGLE = Math.PI / 60;


interface Slidable {
    displayObject: PIXI.DisplayObject;
    translate(dx: number, dy: number): void;
    constrainTranslation(
        offsetX: number, offsetY: number,
        dx: number, dy: number,
        bbox: collision.AABBLike,
    ): [number, collision.ConstraintDirection[]];
    intersects(line: collision.Line): boolean;
    whichSide(line: collision.Line): collision.LineSide;
    highlightIntersection(highlight: boolean): void;
}


export interface CutStartEvent {
    start: collision.PointLike,
    reject: () => void,
}

export interface CutDrawnEvent {
    valid: boolean,
    hideButtons: () => void,
}

export interface CutCommitEvent {
    commit: true,
    state: any,
}

export interface CutCancelEvent {
    commit: false,
}

export interface SlideFinishEvent {
    showButtons: () => void,
}


export type CutFinishEvent = CutCommitEvent | CutCancelEvent;


let boundPointerLockEvent = false;
let pointerLockHandler = () => false;

function onPointerLock(game: Phaser.Game, predicate: () => boolean) {
    pointerLockHandler = predicate;
    if (!boundPointerLockEvent) {
        game.canvas.addEventListener("mousedown", function() {
            if (pointerLockHandler()) {
                game.input.mouse.requestPointerLock();
            }
        });
        boundPointerLockEvent = true;
    }
}

class CutButton {
    private game: Phaser.Game;
    private group: Phaser.Group;
    private label: i18n.Text;
    private button: Phaser.Button;
    private currentTween: Phaser.Tween | null;

    constructor(game: Phaser.Game, label: string, sprite: string, callback: () => void) {
        this.game = game;
        this.group = this.game.add.group();

        this.label = i18n.label(game, 0, -32, label, "font-small", 24);
        this.button = this.game.add.button(0, 0, sprite, () => {
            AudioEngine.get().play("button_click");
            callback();
        }, this, 1, 0, 2, 1);
        this.button.width = 58.5;
        this.button.height = 60;

        this.group.add(this.label);
        this.group.add(this.button);
        this.label.anchor.set(0.5, 0.5);
        this.button.anchor.set(0.5, 0.5);

        this.group.pivot.set(0.5, 0.5);
        this.group.visible = false;

        this.currentTween = null;

        this.button.inputEnabled = true;

        let labelHoverTween: Phaser.Tween | null = null;
        this.button.events.onInputOver.add(() => {
            AudioEngine.get().play("button_hover");
            if (labelHoverTween) labelHoverTween.stop();
            labelHoverTween = this.game.add.tween(this.label).to({
                alpha: 1.0,
            }, 200, Phaser.Easing.Cubic.InOut).start();

            this.button.alpha = 1.0;
        });
        this.button.events.onInputOut.add(() => {
            this.button.alpha = 0.8;

            if (labelHoverTween) labelHoverTween.stop();
            labelHoverTween = this.game.add.tween(this.label).to({
                alpha: 0.0,
            }, 200, Phaser.Easing.Cubic.InOut).start();
        });
        cursor.instance().addClickable(this.button.input);
    }

    fadeIn() {
        if (this.currentTween) this.currentTween.stop();
        this.group.visible = true;
        this.group.alpha = 0.0;
        this.label.alpha = 0.0;

        const tween = this.game.add.tween(this.group).to({
            alpha: 0.8,
        }, 500, Phaser.Easing.Cubic.InOut).start();
        tween.onComplete.add(() => {
            this.currentTween = null;
            this.group.alpha = 1.0;
            this.button.inputEnabled = true;
        });
        this.currentTween = tween;
        return tween;
    }

    fadeOut() {
        if (this.currentTween) this.currentTween.stop();
        this.button.inputEnabled = false;
        const tween = this.game.add.tween(this.group).to({
            alpha: 0.0,
        }, 400, Phaser.Easing.Cubic.InOut).start();
        tween.onComplete.add(() => {
            this.group.visible = false;
        });
        this.currentTween = tween;
    }

    setPosition(x: number, y: number) {
        this.group.position.setTo(x, y);
    }
}

abstract class SubState {
    state: CutState;

    constructor(state: CutState) {
        this.state = state;
    }

    get name() { return "substate"; }

    get bbox() { return this.state.bbox; }
    get rejected() { return this.state.rejected; }
    set rejected(v: boolean) { this.state.rejected = v; }
    get leftGroup() { return this.state.leftGroup; }
    get rightGroup() { return this.state.rightGroup; }

    updateClick(pointer: Phaser.Pointer) {}
    update(pointerDown: boolean, x: number, y: number, dx: number, dy: number) {}
    serializeState(): any {}
    exit() {}
}

export class Normal extends SubState {
    dragStart: Phaser.Point | null = null;
    lastMouse: Phaser.Point | null = null;

    constructor(state: CutState) {
        super(state);
        this.state.clearLine();
        this.state.madeSlide = false;
    }

    get name() { return "normal"; }

    update(pointerDown: boolean, x: number, y: number, dx: number, dy: number) {
        if (pointerDown) {
            if (this.state.rejected)
                return;
            if (x < this.bbox.x ||
                x > this.bbox.x + this.bbox.width ||
                y < this.bbox.y ||
                y > this.bbox.y + this.bbox.height) {
                this.state.reset();
                this.state.clearLine();
                this.state.rejected = true;
                return;
            }
            if (this.dragStart === null) {
                this.dragStart = new Phaser.Point(x, y);
                this.lastMouse = this.dragStart.clone();
                this.state.redrawIndicator(0xAAAAAA, this.dragStart, this.lastMouse);

                logging.logAction(logging.Action.PreCut, this.state.currentState());
            }
            else if (Phaser.Point.distance(this.dragStart, new Phaser.Point(x, y)) > 15) {
                this.state.enter(new Cutting(this.state, this.dragStart));
            }
        }
        else {
            this.rejected = false;
            this.dragStart = null;
            this.state.clearLine();
        }
    }

    serializeState(): any {
        return {};
    }
}

class Cutting extends SubState {
    dragStart: Phaser.Point;
    lastMouse: Phaser.Point;
    lineSlope: collision.Slope;
    lineValid: boolean;
    badCutLabel: i18n.Text;
    warningVisible: boolean = false;

    constructor(state: CutState, start: Phaser.Point) {
        super(state);

        this.dragStart = start;
        this.lastMouse = start.clone();
        this.lineSlope = { dx: 0, dy: 0 };
        this.lineValid = true;

        this.badCutLabel = i18n.label(
            this.state.game, 0, 0,
            i18n._("Can't cut through objects"),
            "font-small", 32);
        this.badCutLabel.anchor.setTo(0.0, 0.0);
        this.badCutLabel.position.setTo(224, 32);
        this.badCutLabel.visible = false;

        logging.logAction(logging.Action.StartCut, this.state.currentState());
        this.state.onCutStart.dispatch({
            start: this.dragStart.clone(),
            reject: () => {
                this.state.reset();
                this.state.rejected = true;
                this.state.enter(new Normal(this.state));
            },
        });
    }

    get name() { return "cutting"; }

    update(pointerDown: boolean, x: number, y: number, lockDx: number, lockDy: number) {
        if (this.state.rejected) {
            this.state.enter(new Normal(this.state));
            return;
        }
        let dx = x - this.dragStart.x;
        let dy = y - this.dragStart.y;
        let angleSteps = Math.round(Math.atan2(dy, dx) / SNAP_ANGLE);
        let angle = SNAP_ANGLE * angleSteps;
        dx = Math.cos(angle);
        dy = Math.sin(angle);
        if (Math.abs(dx) < 0.01) {
            dx = 0;
        }
        if (Math.abs(dy) < 0.01) {
            dy = 0;
        }
        this.lineSlope = { dx: dx, dy: dy };
        this.lastMouse.setTo(x, y);

        let intersectsPlatform = false;
        let blockingPlatform = null;
        const line = new collision.Line(this.dragStart, this.lineSlope);
        for (const platform of this.state.objects) {
            if (platform.intersects(line)) {
                intersectsPlatform = true;
                blockingPlatform = platform.displayObject;
                platform.highlightIntersection(true);
            }
            else {
                platform.highlightIntersection(false);
            }
        }
        this.lineValid = !intersectsPlatform;
        this.state.redrawLine(this.lineValid, this.dragStart, this.lineSlope);
        this.state.redrawIndicator(
            this.lineValid ? CUT_LINE_COLOR_VALID : CUT_LINE_COLOR_INVALID,
            this.dragStart, this.lastMouse);

        // hide the warning label if we're no longer intersecting
        if (this.warningVisible && this.lineValid){
            this.warningVisible = false;
            this.state.game.add.tween(this.badCutLabel).to({
                alpha: 0,
            }, 300, Phaser.Easing.Cubic.Out).start();
        }

        if (!this.lineValid){
            if (!this.warningVisible){
                this.warningVisible = true;
                this.badCutLabel.visible = true;
                this.badCutLabel.alpha = 0;
                this.state.game.add.tween(this.badCutLabel).to({
                    alpha: 1,
                }, 300, Phaser.Easing.Cubic.In).start();
            }
        }

        if (!pointerDown) {
            this.finish();
        }
    }

    finish() {
        // Released the mouse button
        if (this.lineValid) {
            const lineStart = this.dragStart.clone();
            AudioEngine.get().play("slash");
            AudioEngine.get().switchTo("filtered");
            const [tween, leftSide] = this.state.pullApart(
                0.0, 1.0,
                lineStart, this.lineSlope);
            tween.onComplete.add(() => {
                const state = new Cut(this.state, leftSide, lineStart, this.lineSlope);
                this.state.enter(state);
                logging.logAction(logging.Action.DrewCut, this.state.currentState());
                this.state.onCutDrawn.dispatch({
                    valid: true,
                    hideButtons: state.hideButtons.bind(state),
                });
            });
        }
        else {
            // remove label if we finish the cut while there's a warning
            if (this.warningVisible){
                this.warningVisible = false;
                this.state.game.add.tween(this.badCutLabel).to({
                    alpha: 0,
                }, 300, Phaser.Easing.Cubic.Out).start();
            }

            this.state.enter(new Normal(this.state));

            logging.logAction(logging.Action.InvalidCut, this.state.currentState());
            this.state.onCutDrawn.dispatch({
                valid: false,
                hideButtons: function(){},
            });
        }
        for (let platform of this.state.objects) {
            platform.highlightIntersection(false);
        }
    }

    serializeState(): any {
        return {
            dragStart: { x: this.dragStart.x, y: this.dragStart.y },
            lastMouse: { x: this.lastMouse.x, y: this.lastMouse.y },
            lineSlope: { x: this.lineSlope.dx, y: this.lineSlope.dy },
            lineValid: this.lineValid,
        };
    }
}

export class Sliding extends SubState {
    cut: Cut;
    mouseStart: Phaser.Point;
    lastMouse: Phaser.Point;
    snapback: [collision.PointLike, collision.PointLike] | undefined;
    snapbackDistance: number = 1.0;
    warningLabel: i18n.Text;


    constructor(state: CutState, cut: Cut, mouseStart: Phaser.Point) {
        super(state);
        this.cut = cut;
        this.mouseStart = mouseStart;
        this.lastMouse = this.mouseStart.clone();
        this.warningLabel = i18n.label(
            this.state.game, 0, 0,
            i18n._("Objects must stay visible"),
            "font-small", 32);
        this.warningLabel.visible = false;
        logging.logAction(logging.Action.StartSlide,
                          this.state.currentState());
        this.state.onSlideStart.dispatch();
    }

    get name() { return "sliding"; }

    get lineSlope() { return this.cut.lineSlope; }
    get lineStart() { return this.cut.lineStart; }
    get leftSide() { return this.cut.leftSide; }
    get leftBase() { return this.cut.leftBase; }
    get rightBase() { return this.cut.rightBase; }

    update(pointerDown: boolean, x: number, y: number, dx: number, dy: number) {
        if (pointerDown) {
            this.processSlide(x, y, dx, dy);
        }
        else {
            if (this.snapback !== undefined) {
                const snapback = this.snapback;
                const status = new Animating(this.cut);
                this.state.status = status;

                const dxLeft = this.leftGroup.x - this.snapback[0].x;
                const dyLeft = this.leftGroup.y - this.snapback[0].y;
                const dxRight = this.rightGroup.x - this.snapback[1].x;
                const dyRight = this.rightGroup.y - this.snapback[1].y;
                const tween = this.state.game.add.tween({
                    t: 1,
                }).to({
                    t: 0,
                }, 300, Phaser.Easing.Cubic.Out).onUpdateCallback((tween: Phaser.Tween) => {
                    const t = tween.target.t;

                    this.leftGroup.x = snapback[0].x + t * dxLeft;
                    this.leftGroup.y = snapback[0].y + t * dyLeft;
                    this.rightGroup.x = snapback[1].x + t * dxRight;
                    this.rightGroup.y = snapback[1].y + t * dyRight;
                });
                tween.onComplete.add(() => {
                    this.leftGroup.x = snapback[0].x;
                    this.leftGroup.y = snapback[0].y;
                    this.rightGroup.x = snapback[1].x;
                    this.rightGroup.y = snapback[1].y;
                    this.snapback = undefined;
                    this.state.game.time.events.add(300, () => {
                        if (status.canceled) return;
                        this.state.enter(this.cut);
                        logging.logAction(logging.Action.FinishSlide,
                                          this.state.currentState());
                        this.state.objects.forEach(p => p.highlightIntersection(false));
                        this.state.onSlideFinish.dispatch({
                            showButtons: this.cut.showButtons.bind(this.cut),
                        });
                    });
                });
                tween.start();

                if (this.state.bboxHighlight.top) {
                    this.showWarning(this.bbox.x + this.bbox.width / 2,
                                     this.bbox.y + 20,
                                     0.5, 0.0);
                }
                else if (this.state.bboxHighlight.left) {
                    this.showWarning(this.bbox.x,
                                     this.bbox.y + this.bbox.height / 2,
                                     0, 0.5);
                }
                else if (this.state.bboxHighlight.right) {
                    this.showWarning(this.bbox.x + this.bbox.width,
                                     this.bbox.y + this.bbox.height / 2,
                                     1.0, 0.5);
                }
                else if (this.state.bboxHighlight.bottom) {
                    this.showWarning(this.bbox.x + this.bbox.width / 2,
                                     this.bbox.y + this.bbox.height - 20,
                                     0.5, 1.0);
                }
            }
            else {
                this.state.enter(this.cut);
                logging.logAction(logging.Action.FinishSlide,
                                  this.state.currentState());
                this.state.onSlideFinish.dispatch({
                    showButtons: this.cut.showButtons.bind(this.cut),
                });
            }
        }
    }

    showWarning(x: number, y: number, anchorX: number, anchorY: number) {
        this.warningLabel.anchor.setTo(anchorX, anchorY);
        this.warningLabel.position.setTo(x, y);
        this.warningLabel.visible = true;
        this.warningLabel.alpha = 0;
        this.state.game.add.tween(this.warningLabel).to({
            alpha: 1,
        }, 300, Phaser.Easing.Cubic.In).yoyo(true, 500).start().onComplete.add(() => {
            this.warningLabel.visible = false;
        });
    }

    processSlide(endX: number, endY: number, movementX: number, movementY: number) {
        const startX = this.lastMouse.x;
        const startY = this.lastMouse.y;
        let dx = endX - startX + movementX;
        let dy = endY - startY + movementY;

        if (dx === 0.0 && dy === 0.0) {
            return;
        }

        this.state.madeSlide = true;

        const mag1 = Math.sqrt(dx*dx + dy*dy);
        const mag2 = Math.sqrt(this.lineSlope.dx*this.lineSlope.dx +
                               this.lineSlope.dy*this.lineSlope.dy);
        const dot = (dx * this.lineSlope.dx + dy * this.lineSlope.dy)
            / (mag1 * mag2);

        dx = mag1 * this.lineSlope.dx * dot / mag2;
        dy = mag1 * this.lineSlope.dy * dot / mag2;

        // XXX Don't allow very small translations - floating point
        // inaccuracy means platforms can get stuck on screen edges
        // Is there a better way to deal with this?
        if (Math.abs(dx) < 0.1) {
            dx = 0;
        }
        if (Math.abs(dy) < 0.1) {
            dy = 0;
        }

        let affectedPlatforms = [];
        let translationScaling = 1;
        const line = new collision.Line(this.lineStart, this.lineSlope);
        const dragSide = line.whichSide(this.mouseStart);

        // Platforms that have reached a screen edge
        const boundedPlatforms = [];

        const base = dragSide === this.leftSide ? this.leftBase : this.rightBase;
        const curr = dragSide === this.leftSide ? this.leftGroup.position : this.rightGroup.position;
        let offsetX = curr.x - base.x;
        let offsetY = curr.y - base.y;

        this.state.bboxHighlight = {
            top: false,
            bottom: false,
            left: false,
            right: false,
        };
        for (const platform of this.state.objects) {
            if (platform.whichSide(line) === dragSide) {
                let [scale, directions] = platform.constrainTranslation(
                    offsetX, offsetY,
                    dx, dy, this.bbox);

                for (let direction of directions) {
                    this.state.bboxHighlight[direction] = true;
                }

                if (Math.abs(scale) < 0.01) {
                    boundedPlatforms.push(platform);
                    platform.highlightIntersection(true);
                }
                else {
                    platform.highlightIntersection(false);
                }
                translationScaling = Math.min(scale, translationScaling);
                affectedPlatforms.push(platform);
            }
        }

        this.state.redrawBbox();

        translationScaling = Math.max(translationScaling, 0.0);
        if (translationScaling > 0.2 && this.snapback) {
            this.snapback = undefined;
        }
        if (translationScaling < 0.01 || this.snapback !== undefined) {
            if (this.snapback === undefined) {
                this.snapback = [this.leftGroup.position.clone(),
                                 this.rightGroup.position.clone()];
                this.snapbackDistance = 1.0;

                // TODO: log boundedPlatforms, directions
                logging.logAction(logging.Action.SlideMaxRange, this.state.currentState());
                this.state.onSlideMaxRange.dispatch();
            }
            translationScaling = 0.1 / this.snapbackDistance;
            this.snapbackDistance *= 1.1;
        }

        dx *= translationScaling;
        dy *= translationScaling;

        if (affectedPlatforms.length > 0) {
            const platform = affectedPlatforms[0];
            platform.displayObject.parent.position.x += dx;
            platform.displayObject.parent.position.y += dy;
        }

        this.lastMouse.x = endX;
        this.lastMouse.y = endY;
    }

    serializeState(): any {
        return {
            cut: this.cut.serializeState(),
            mouseStart: { x: this.mouseStart.x, y : this.mouseStart.y },
            lastMouse: { x: this.lastMouse.x, y: this.lastMouse.y },
        };
    }
}

export class Cut extends SubState {
    doneButton: CutButton;
    cancelButton: CutButton;

    lineStart: Phaser.Point;
    lineSlope: collision.Slope;
    leftSide: collision.LineSide;

    leftBase: Phaser.Point;
    rightBase: Phaser.Point;

    constructor(state: CutState, leftSide: collision.LineSide,
                lineStart: Phaser.Point, lineSlope: collision.Slope) {
        super(state);
        this.doneButton = new CutButton(this.state.game, i18n._("DONE"), "done", () => {
            this.finish(this.state.madeSlide);
        });
        this.cancelButton = new CutButton(this.state.game, i18n._("CANCEL"), "cancel", () => {
            this.finish(false);
        });
        this.leftSide = leftSide;

        let offsetX = 32 * lineSlope.dx;
        let offsetY = 32 * lineSlope.dy;
        if (offsetX > 0) {
            offsetX *= -1;
            offsetY *= -1;
        }

        const [left, right] = this.state.positionLine(lineStart, lineSlope);
        let lastCenterX = 0.5 * (left.x + right.x);
        let lastCenterY = 0.5 * (left.y + right.y);
        this.doneButton.setPosition(lastCenterX + offsetX, lastCenterY + offsetY);
        this.doneButton.fadeIn();
        this.cancelButton.setPosition(lastCenterX - offsetX, lastCenterY - offsetY);
        this.cancelButton.fadeIn();

        this.lineStart = lineStart;
        this.lineSlope = lineSlope;
        this.leftBase = this.leftGroup.position.clone();
        this.rightBase = this.rightGroup.position.clone();
    }

    get name() { return "cut"; }

    hideButtons() {
        this.doneButton.fadeOut();
        this.cancelButton.fadeOut();
    }

    showButtons() {
        this.doneButton.fadeIn();
        this.cancelButton.fadeIn();
    }

    update(pointerDown: boolean, x: number, y: number, lockDx: number, lockDy: number) {
        const clickedVoid = (this.state.lineIndicator as any).containsPoint({ x: x, y: y });

        if (!clickedVoid) {
            cursor.instance().defaultCursor = "move";
        }
        else {
            cursor.instance().defaultCursor = "default";
        }

        if (pointerDown) {
            if (clickedVoid) {
                return;
            }
            // Start dragging a side of a cut
            this.state.enter(new Sliding(this.state, this, new Phaser.Point(x, y)));
        }
        else {
            this.state.bboxHighlight.top = false;
            this.state.bboxHighlight.bottom = false;
            this.state.bboxHighlight.left = false;
            this.state.bboxHighlight.right = false;
            this.state.redrawBbox();
        }
    }

    finish(commit: boolean) {
        if (!(this.state.status instanceof Cut || this.state.status instanceof Sliding)) {
            return;
        }

        AudioEngine.get().switchTo("regular");

        if (commit) {
            // Dispatch event so undo handler can preserve
            // pre-translation state
            this.state.onCutFinish.dispatch({
                commit: true,
                state: this.state.currentState(),
            });

            const line = new collision.Line(this.lineStart, this.lineSlope);
            const leftFinal = this.leftGroup.position.clone();
            const rightFinal = this.rightGroup.position.clone();

            const deltaA = { x: leftFinal.x - this.leftBase.x,
                             y: leftFinal.y - this.leftBase.y };
            const deltaB = { x: rightFinal.x - this.rightBase.x,
                             y: rightFinal.y - this.rightBase.y };
            for (const platform of this.state.objects) {
                if (platform.whichSide(line) === this.leftSide) {
                    platform.translate(deltaA.x, deltaA.y);
                }
                else {
                    platform.translate(deltaB.x, deltaB.y);
                }
                platform.highlightIntersection(false);
            }

            this.leftGroup.position.setTo(this.leftBase.x, this.leftBase.y);
            this.rightGroup.position.setTo(this.rightBase.x, this.rightBase.y);
            logging.logAction(logging.Action.FinishCut, this.state.currentState(true));
        }
        else {
            for (const platform of this.state.objects) {
                platform.highlightIntersection(false);
            }
            logging.logAction(logging.Action.CanceledCut, this.state.currentState());
            this.state.onCutFinish.dispatch({
                commit: false,
                state: this.state.currentState(),
            });
        }

        this.doneButton.fadeOut();
        this.cancelButton.fadeOut();

        const [tween, _] = this.state.pullApart(1.0, 0.0, this.lineStart, this.lineSlope, true);
        tween.onComplete.add(() => {
            this.state.reset();
            this.state.enter(new Normal(this.state));
        });
    }

    serializeState(): any {
        return {
            lineStart: { x: this.lineStart.x, y: this.lineStart.y },
            lineSlope: this.lineSlope,
            sideA: this.leftSide,
            sideB: this.leftSide === "left" ? "right" : "left",

            leftBase: { x: this.leftBase.x, y: this.leftBase.y },
            rightBase: { x: this.leftBase.x, y: this.leftBase.y },
            leftCurr: { x: this.leftGroup.x, y: this.leftGroup.y },
            rightCurr: { x: this.leftGroup.x, y: this.leftGroup.y },
        };
    }
}

type Status = SubState;

export class Animating {
    innerState: Status | null;
    canceled: boolean;

    constructor(status?: Status) {
        this.canceled = false;
        if (status) {
            this.innerState = status;
        }
        else {
            this.innerState = null;
        }
    }

    cancel() {
        this.canceled = true;
    }

    get name() {
        return "animating";
    }

    serializeState() {
        return this.innerState === null ? {} : this.innerState.serializeState();
    }
}

export class CutState {
    debugState: Phaser.Text;

    game: Phaser.Game;

    /**
     * Is the player platforming, cutting, or sliding?
     *
     * normal - platforming
     * cutting - drawing a cut
     * sliding - sliding one side of a cut
     * cut - drew a cut, waiting to begin sliding
     */
    status: Status | Animating;

    line: Phaser.Sprite;
    lineIndicator: Phaser.Graphics;
    bboxIndicator: Phaser.Graphics;

    bbox: collision.AABBLike;
    madeSlide = false;

    parentGroup: Phaser.Group;
    leftGroup: Phaser.Group;
    rightGroup: Phaser.Group;
    objects: Slidable[];

    bgAlpha: number;

    rejected: boolean = false;

    bboxHighlight = {
        top: false,
        right: false,
        bottom: false,
        left: false,
    };

    serializeState: () => any;

    /**
     * When the user starts drawing a cut.
     *
     * Event object:
     * start - A PointLike indicating the origin of the cut.
     * reject - A function that when called, rejects the cut.
     */
    onCutStart: Phaser.Signal;
    /**
     * When the user finishes drawing a cut.
     *
     * Event object:
     * valid - Whether the cut was valid.
     */
    onCutDrawn: Phaser.Signal;
    /**
     * When the user starts sliding a side.
     */
    onSlideStart: Phaser.Signal;
    onSlideMaxRange: Phaser.Signal;
    onSlideFinish: Phaser.Signal;
    onCutFinish: Phaser.Signal;

    constructor(
        parentGroup: Phaser.Group,
        lineSprite: Phaser.Sprite,
        lineIndicator: Phaser.Graphics,
        bboxIndicator: Phaser.Graphics,
        bbox: collision.AABBLike,
        serializeState: () => any,
    ) {
        this.bgAlpha = 1.0;

        this.game = parentGroup.game;

        this.parentGroup = parentGroup;
        this.leftGroup = parentGroup.game.add.group(parentGroup);
        this.rightGroup = parentGroup.game.add.group(parentGroup);

        this.line = lineSprite;
        this.line.pivot.set(0.5, 0);
        this.line.anchor.set(0.5, 0);
        this.lineIndicator = lineIndicator;
        this.bboxIndicator = bboxIndicator;

        this.bbox = bbox;

        this.objects = [];

        if (!logging.PRODUCTION) {
            this.debugState = this.game.add.text(this.game.width - 100, 0, "", {
                fill: "white",
                fontSize: 12,
            });
        }

        this.serializeState = serializeState;

        onPointerLock(this.game, () => {
            return false;
            // TODO: reenable when we can make it less annoying
            // TODO: this needs to handle undo button etc too
            // return this.status === "cut" &&
            //     !this.doneButton.mouseOver &&
            //     !this.cancelButton.mouseOver;
        });

        this.onCutStart = new Phaser.Signal();
        this.onCutDrawn = new Phaser.Signal();
        this.onSlideStart = new Phaser.Signal();
        this.onSlideMaxRange = new Phaser.Signal();
        this.onSlideFinish = new Phaser.Signal();
        this.onCutFinish = new Phaser.Signal();

        this.status = new Normal(this);
    }

    get isCutting() {
        return !(this.status instanceof Normal);
    }

    addObject(obj: Slidable) {
        this.objects.push(obj);
    }

    currentState(includePlatforms=false): any {
        const result: { [key: string]: any } = {
            "status": this.status.name,
            "statusData": this.status.serializeState(),
            // TODO: get the # of cuts made
        };

        if (includePlatforms) {
            result.state = this.serializeState();
        }

        return result;
    }

    offsetFor(thing: Slidable): collision.PointLike {
        if (this.status instanceof Sliding || this.status instanceof Cut) {
            if (this.leftGroup.contains(thing.displayObject)) {
                return { x: this.leftGroup.position.x - this.status.leftBase.x,
                         y: this.leftGroup.position.y - this.status.leftBase.y };
            }
            else if (this.rightGroup.contains(thing.displayObject)) {
                return { x: this.rightGroup.position.x - this.status.rightBase.x,
                         y: this.rightGroup.position.y - this.status.rightBase.y };
            }
        }
        else if (this.status instanceof Animating && this.status.innerState instanceof Cut) {
            if (this.leftGroup.contains(thing.displayObject)) {
                return { x: this.leftGroup.position.x - this.status.innerState.leftBase.x,
                         y: this.leftGroup.position.y - this.status.innerState.leftBase.y };
            }
            else if (this.rightGroup.contains(thing.displayObject)) {
                return { x: this.rightGroup.position.x - this.status.innerState.rightBase.x,
                         y: this.rightGroup.position.y - this.status.innerState.rightBase.y };
            }
        }
        return { x: 0, y: 0 };
    }

    cancel() {
        if (this.status instanceof Cut) {
            this.status.finish(false);
        }
        else if (this.status instanceof Sliding) {
            this.status.cut.finish(false);
        }
        else if (this.status instanceof Cutting) {
            logging.logAction(logging.Action.CanceledCut, this.currentState());
            this.onCutFinish.dispatch({
                commit: false,
                state: this.currentState(),
            });
            this.reset();
            this.status = new Normal(this);
        }
        else if (this.status instanceof Animating) {
            this.status.cancel();
            if (this.status.innerState) {
                this.status = this.status.innerState;
                this.cancel();
            }
            else {
                this.status = new Normal(this);
            }
        }
        else if (this.status instanceof Normal) {
            this.reset();
        }
    }

    flagDead() {
        let cutState: Cut | null = null;
        if (this.status instanceof Cut) {
            cutState = this.status;
        }
        else if (this.status instanceof Sliding) {
            cutState = this.status.cut;
        }
        else if (this.status instanceof Animating && this.status.innerState instanceof Cut) {
            cutState = this.status.innerState;
        }

        if (cutState) {
            cutState.doneButton.fadeOut();
            cutState.cancelButton.fadeOut();
        }
    }

    tryFinish() {
        if (this.status instanceof Cut) {
            this.status.finish(this.madeSlide);
        }
        else if (this.status instanceof Sliding) {
            this.status.cut.finish(this.madeSlide);
        }
    }

    reset() {
        this.leftGroup.removeAll();
        this.rightGroup.removeAll();
        for (const platform of this.objects) {
            this.parentGroup.add(platform.displayObject);
        }

        if (this.status instanceof Animating) this.status.cancel();

        this.clearLine();
        this.parentGroup.scale.x = 1;
        this.parentGroup.scale.y = 1;
        this.parentGroup.position.x = 0;
        this.parentGroup.position.y = 0;
        this.bboxHighlight.top = false;
        this.bboxHighlight.bottom = false;
        this.bboxHighlight.left = false;
        this.bboxHighlight.right = false;
        this.redrawBbox();
    }

    positionLine(
        point: collision.PointLike, slope: collision.Slope
    ): [collision.PointLike, collision.PointLike, number] {
        const { dx, dy } = slope;
        let { x: startX, y: startY } = point;
        let endX = 0;
        let endY = 0;
        let angle = 0;
        if (Math.abs(dx) < FLOAT_EPSILON) {
            endX = startX;
            endY = this.game.height;
            startY = 0;
            angle = 0;
        }
        else if (Math.abs(dy) < FLOAT_EPSILON) {
            endY = startY;
            endX = this.game.width;
            startX = 0;
            angle = -Math.PI / 2;
        }
        else {
            const slope = dy / dx;
            endX = startX + (this.game.height - startY) / slope;
            startX = startX - startY / slope;
            startY = 0;
            endY = this.game.height;

            if (startX > endX) {
                [startX, endX] = [endX, startX];
                [startY, endY] = [endY, startY];
            }

            if (startX < 0) {
                startX = 0;
                startY = endY - slope * endX;
            }

            if (endX > this.game.width) {
                endX = this.game.width;
                endY = startY + slope * (this.game.width - startX);
            }

            angle = Math.atan2(dy, dx) - Math.PI / 2;

            if (angle < -Math.PI / 2) {
                angle += Math.PI;
            }
        }

        return [
            { x: startX, y: startY },
            { x: endX, y: endY },
            angle,
        ];
    }

    redrawLine(lineValid: boolean, mouseStart: Phaser.Point, lineSlope: collision.Slope) {
        let [
            { x: startX, y: startY },
            { x: endX, y: endY },
            angle
        ] = this.positionLine(mouseStart, lineSlope);

        const color = lineValid ? CUT_LINE_COLOR_VALID : CUT_LINE_COLOR_INVALID;
        if (startY <= endY) {
            this.line.position.setTo(startX, startY);
        }
        else {
            this.line.position.setTo(endX, endY);
        }
        this.line.visible = true;
        this.line.rotation = angle;
        this.line.height = Math.sqrt((endX - startX)**2 + (endY - startY)**2);
        this.line.tint = color;
    }

    redrawIndicator(color: number, start: Phaser.Point, end: Phaser.Point) {
        this.lineIndicator.clear();
        this.lineIndicator.lineStyle(0);
        this.lineIndicator.beginFill(color);
        this.lineIndicator.drawCircle(start.x, start.y, 8);
        this.lineIndicator.drawCircle(end.x, end.y, 8);
    }

    redrawBbox() {
        const lineWidth = 5.0;

        this.bboxIndicator.clear();
        this.bboxIndicator.moveTo(this.bbox.x, this.bbox.y);
        this.bboxIndicator.lineStyle(lineWidth, 0xFF0000, this.bboxHighlight.top ? lineWidth : 0.0);
        this.bboxIndicator.lineTo(this.bbox.width, this.bbox.y);

        this.bboxIndicator.lineStyle(lineWidth, 0xFF0000, this.bboxHighlight.right ? lineWidth : 0.0);
        this.bboxIndicator.lineTo(this.bbox.width, this.bbox.height);

        this.bboxIndicator.lineStyle(lineWidth, 0xFF0000, this.bboxHighlight.bottom ? lineWidth : 0.0);
        this.bboxIndicator.lineTo(this.bbox.x, this.bbox.height);

        this.bboxIndicator.lineStyle(lineWidth, 0xFF0000, this.bboxHighlight.left ? lineWidth : 0.0);
        this.bboxIndicator.lineTo(this.bbox.x, this.bbox.y);
    }

    clearLine() {
        this.line.visible = false;
        this.lineIndicator.clear();
    }

    get physicsPaused(): boolean {
        return !(this.status instanceof Normal);
    }

    updateClick(pointer: Phaser.Pointer) {
        if (!(this.status instanceof Animating)) {
            this.status.updateClick(pointer);
        }
    }

    update(pointerDown: boolean, x: number, y: number, dx: number, dy: number) {
        if (!(this.status instanceof Animating)) {
            this.status.update(pointerDown, x, y, dx, dy);
        }

        if (!pointerDown) {
            this.game.input.mouse.releasePointerLock();
        }

        if (!logging.PRODUCTION) {
            this.debugState.text = this.status instanceof Animating ?
                "animating" : this.status.name;
        }
    }

    pullApart(
        startValue: number, endValue: number,
        lineStart: Phaser.Point, lineSlope: collision.Slope,
        reset?: boolean
    ): [Phaser.Tween, collision.LineSide] {
        const line = new collision.Line(lineStart, lineSlope);

        const perpendicular = { dx: -lineSlope.dy, dy: lineSlope.dx };
        const pointA = { x: lineStart.x + perpendicular.dx,
                         y: lineStart.y + perpendicular.dy };

        const sideA = line.whichSide(pointA);
        for (const platform of this.objects) {
            if (platform.whichSide(line) === sideA) {
                this.leftGroup.add(platform.displayObject);
            }
            else {
                this.rightGroup.add(platform.displayObject);
            }
        }

        return [this.animatePullApart(
            startValue, endValue,
            lineStart, lineSlope, perpendicular,
            reset,
        ), sideA];
    }

    private animatePullApart(
        startValue: number, endValue: number,
        lineStart: Phaser.Point, lineSlope: collision.Slope,
        perpendicular: collision.Slope,
        reset?: boolean
    ): Phaser.Tween {
        const [left, right] = this.positionLine(lineStart, lineSlope);
        const targetScaleX = this.game.width / (this.game.width + 2 * endValue * CUT_WIDTH);
        const targetScaleY = this.game.height / (this.game.height + 2 * endValue * CUT_WIDTH);
        this.game.add.tween(this.parentGroup.scale).to({
            x: targetScaleX,
            y: targetScaleY,
        }, 400, Phaser.Easing.Cubic.Out).start();

        this.game.add.tween(this.leftGroup.position).to({
            x: endValue * CUT_WIDTH * perpendicular.dx,
            y: endValue * CUT_WIDTH * perpendicular.dy,
        }, 400, Phaser.Easing.Cubic.Out).start();

        this.game.add.tween(this.rightGroup.position).to({
            x: -endValue * CUT_WIDTH * perpendicular.dx,
            y: -endValue * CUT_WIDTH * perpendicular.dy,
        }, 400, Phaser.Easing.Cubic.Out).start();

        this.game.add.tween(this.parentGroup.position).to({
            x: endValue * CUT_WIDTH,
            y: endValue * CUT_WIDTH,
        }, 400, Phaser.Easing.Cubic.Out).start();

        const update = (offset: number) => {
            this.lineIndicator.clear();
            this.lineIndicator.beginFill(0x000000);

            let slope = lineSlope;
            let pointA = { x: lineStart.x + offset * perpendicular.dx,
                           y: lineStart.y + offset * perpendicular.dy };
            let pointB = { x: lineStart.x - offset * perpendicular.dx,
                           y: lineStart.y - offset * perpendicular.dy };

            let [point1a, point1b, ] = this.positionLine(pointA, slope);
            let [point2a, point2b, ] = this.positionLine(pointB, slope);

            if (Math.abs(point1a.x - point2a.x) > FLOAT_EPSILON &&
                Math.abs(point1a.y - point2a.y) > FLOAT_EPSILON) {
                point1a.x -= Math.abs(100 * slope.dx);
                point2a.x -= Math.abs(100 * slope.dx);
                point1a.y -= (slope.dx > 0 ? 100 : -100) * slope.dy;
                point2a.y -= (slope.dx > 0 ? 100 : -100) * slope.dy;
            }

            if (Math.abs(point1b.x - point2b.x) > FLOAT_EPSILON &&
                Math.abs(point1b.y - point2b.y) > FLOAT_EPSILON) {
                point1b.x += Math.abs(100 * slope.dx);
                point2b.x += Math.abs(100 * slope.dx);
                point1b.y += (slope.dx > 0 ? 100 : -100) * slope.dy;
                point2b.y += (slope.dx > 0 ? 100 : -100) * slope.dy;
            }

            this.lineIndicator.moveTo(point1a.x, point1a.y);
            this.lineIndicator.lineTo(point1b.x, point1b.y);
            this.lineIndicator.lineTo(point2b.x, point2b.y);
            this.lineIndicator.lineTo(point2a.x, point2a.y);
            this.lineIndicator.lineTo(point1a.x, point1a.y);

            this.lineIndicator.endFill();

            this.bgAlpha = 1.0 - 0.3 * offset / CUT_WIDTH;
        };

        if (reset) {
            update(startValue);
        }
        this.status = new Animating();

        const tween = this.parentGroup.game.add.tween({
            offset: startValue * CUT_WIDTH,
        }).to({
            offset: endValue * CUT_WIDTH,
        }, 400, Phaser.Easing.Cubic.Out).onUpdateCallback(function(tween: Phaser.Tween) {
            update(tween.target.offset);
        });
        tween.start();

        return tween;
    }

    enter(state: SubState) {
        if (this.status instanceof SubState) this.status.exit();
        this.status = state;
    }
}



// WEBPACK FOOTER //
// ./src/cutandslide.ts